import {
  Box,
  type BoxProps,
  Grid,
  ResponsiveContext,
  Banner,
} from '@kluein/klue-ui';
import { motion, AnimatePresence } from 'framer-motion';
import { memo, useContext, useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useViewObserver } from 'hooks/use-view-observer';
import store from 'store';
import { getRivalGroupRivals } from 'store/models/dashboard/dashboard.model';
import TEST_IDS from 'test-ids';

import { RivalGroupItem } from '../rival-group-item';
import {
  HEIGHT_WHEN_RIVAL_LINK_HIDDEN,
  type RivalGroupItemType,
} from '../rival-group-item/RivalGroupItem';

import type { DashboardSortType } from 'api/endpoints/rivals.types';

export type RivalGridProps = {
  rivalGroupId?: number;
  sort: DashboardSortType;
  rowOrder?: number;
};

const Status = {
  error: 'error',
} as const;

const RivalGrid = memo(({ rivalGroupId, sort, rowOrder }: RivalGridProps) => {
  const placeholderHeight = HEIGHT_WHEN_RIVAL_LINK_HIDDEN;
  const screenSize = useContext(ResponsiveContext);

  const rivalsById = useSelector(store.select.rivals.byId);
  const rivalGroupsById = useSelector(store.select.rivalGroups.byId);
  const rivalGroupRivals = useMemo(
    () => getRivalGroupRivals(rivalsById, rivalGroupsById, rivalGroupId, sort),
    [rivalsById, rivalGroupsById, rivalGroupId, sort],
  );

  return (
    <Grid
      margin={{ bottom: 'xxlarge' }}
      columns={screenSize !== 'xxsmall' ? '215px' : '100%'}
      gap="large"
      align="center"
      fill={false}
      data-test-id={TEST_IDS.dashboard.rivalGroup.grid}
    >
      <AnimatePresence>
        {rivalGroupRivals?.map((rival, index) => (
          <motion.div
            key={rival.id || `loading-${index}`}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
          >
            <VisibleRivalGroupItem
              rival={rival}
              rivalGroupId={rivalGroupId}
              placeholderHeight={placeholderHeight}
              order={index + 1}
              rowOrder={rowOrder}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </Grid>
  );
});

const VisibleRivalGroupItem = ({
  rivalGroupId,
  rival,
  placeholderHeight,
  order,
  rowOrder,
}: RivalGroupItemType & { placeholderHeight: BoxProps['height'] }) => {
  const { observe, inView } = useViewObserver({
    threshold: 0.1,
    delay: 0,
    rootMargin: '500px',
  });
  return (
    <Box ref={observe}>
      {rival && inView ? (
        <RivalGroupItem
          rival={rival}
          rivalGroupId={rivalGroupId}
          order={order}
          rowOrder={rowOrder}
        />
      ) : (
        <Box height={placeholderHeight} />
      )}
    </Box>
  );
};

const FallbackComponent = () => {
  const { t } = useTranslation();
  return (
    <Box pad={{ top: 'medium', bottom: 'xlarge' }}>
      <Banner
        status={Status.error}
        message={t('Dashboard:errorRivalsGroup')}
        show
        showIcon
      />
    </Box>
  );
};

export default withErrorBoundary(RivalGrid, { FallbackComponent });
